// Vendor Performance Scorecard tab (UN EOI "Third-Party Performance Scorecard").
// Business-owner review: question catalog served from the global dbo reference table
// (DB-configurable — Axaris staff manage questions directly in the database; users only see
// them), rated 1-5 (Unsatisfactory..Excellent), scored server-side (theme averages, weighted
// overall, trend, recommendation label).
// Displayed beside the vendor's cyber-risk and financial figures — the Axaris differentiator.
// The question-answering form and reviewer reassignment live in VendorPerformanceReviewModal,
// opened from the vendor header next to What-If Explorer — this tab covers everything else.
import React, { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const LABEL_CFG = {
    "Preferred Vendor":  "bg-green-100 text-green-800",
    "Strategic Vendor":  "bg-emerald-50 text-emerald-700",
    "Acceptable":        "bg-blue-50 text-blue-700",
    "Needs Improvement": "bg-yellow-100 text-yellow-800",
    "High Concern":      "bg-red-100 text-red-800",
};

const TREND_CFG = {
    Improved: { arrow: "▲", cls: "text-green-600" },
    Declined: { arrow: "▼", cls: "text-red-600" },
    Stable:   { arrow: "▬", cls: "text-gray-500" },
    New:      { arrow: "●", cls: "text-blue-500" },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function VendorPerformanceScorecardTab({ sc, canManage }) {
    const { catalog, loading, error, actionError, setActionError, users,
            completed, latest, open, chartData, data, issueNow, setCadence } = sc;

    // Issue-time reviewer prompt: "" = backend default (the vendor's assigned business owner).
    const [showIssueModal, setShowIssueModal] = useState(false);
    const [issueReviewer, setIssueReviewer] = useState("");
    const [issuing, setIssuing] = useState(false);

    if (loading) return <div className="py-10 text-sm text-gray-400 text-center">Loading performance scorecards…</div>;
    if (error)   return <div className="py-10 text-sm text-red-500 text-center">{error}</div>;

    const handleIssue = async () => {
        setIssuing(true);
        const ok = await issueNow(issueReviewer || null);
        setIssuing(false);
        if (ok) setShowIssueModal(false);
    };

    return (
        <div className="space-y-5">
            {actionError && (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-xs text-red-700">{actionError}</span>
                    <button onClick={() => setActionError(null)} className="text-xs font-semibold text-red-400 hover:text-red-700">✕</button>
                </div>
            )}

            {/* ── Hero: latest business-owner score ─────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Performance Score</div>
                    <div className="text-3xl font-bold text-indigo-700">{latest ? Number(latest.overallScore).toFixed(1) : "—"}<span className="text-sm text-gray-400 font-normal"> / 5</span></div>
                    {latest?.recommendationLabel && (
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${LABEL_CFG[latest.recommendationLabel] || "bg-gray-100 text-gray-600"}`}>
                            {latest.recommendationLabel}
                        </span>
                    )}
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Trend</div>
                    {latest?.trend ? (
                        <div className={`text-2xl font-bold ${TREND_CFG[latest.trend]?.cls || "text-gray-500"}`}>
                            {TREND_CFG[latest.trend]?.arrow} {latest.trend}
                        </div>
                    ) : <div className="text-2xl font-bold text-gray-300">—</div>}
                    <div className="text-xs text-gray-400 mt-1">vs previous review</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Last Review</div>
                    <div className="text-lg font-bold text-gray-700">{latest ? latest.reviewPeriod : "None yet"}</div>
                    <div className="text-xs text-gray-400 mt-1">{latest ? `by ${latest.reviewerName || "—"} · ${fmtDate(latest.completedAt)}` : "no completed reviews"}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Review Cadence</div>
                    {canManage ? (
                        <select value={data?.cadenceOverride ?? ""} onChange={e => setCadence(e.target.value)}
                            title="Default cadence — tier-based, tightened automatically if the vendor's active risk is Critical or High"
                            className="mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white w-full">
                            <option value="">Default ({data?.cadenceMonths} months)</option>
                            <option value="3">Every 3 months</option>
                            <option value="6">Every 6 months</option>
                            <option value="12">Every 12 months</option>
                        </select>
                    ) : (
                        <div className="text-lg font-bold text-gray-700">Every {data?.cadenceMonths} months</div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">auto-issued to the business owner</div>
                </div>
            </div>

            {/* ── Trend chart ────────────────────────────────────────────────── */}
            {chartData.length >= 2 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">Performance Over Time</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: -18 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                            <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                            <Line type="monotone" dataKey="overall" name="Overall" stroke="#4338ca" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── No open scorecard ──────────────────────────────────────────── */}
            {!open && canManage && (
                <div className="bg-white border border-dashed border-gray-200 rounded-xl p-5 flex items-center justify-between">
                    <span className="text-sm text-gray-500">No open performance review. The next one auto-issues on the review cadence.</span>
                    <button onClick={() => { setIssueReviewer(""); setShowIssueModal(true); }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 flex-shrink-0">
                        Issue Scorecard Now
                    </button>
                </div>
            )}

            {/* ── Issue modal: pick the reviewer at issue time ───────────────── */}
            {showIssueModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-[420px]">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold">Issue Performance Scorecard</h2>
                            <button onClick={() => setShowIssueModal(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500">✕</button>
                        </div>
                        <div className="p-5">
                            {actionError && (
                                <div className="flex items-center justify-between gap-3 px-3 py-2 mb-3 bg-red-50 border border-red-200 rounded-lg">
                                    <span className="text-xs text-red-700">{actionError}</span>
                                    <button onClick={() => setActionError(null)} className="text-xs font-semibold text-red-400 hover:text-red-700">✕</button>
                                </div>
                            )}
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Assign reviewer</label>
                            <select value={issueReviewer} onChange={e => setIssueReviewer(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 mb-1.5">
                                <option value="">Vendor's assigned business owner (default)</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                            </select>
                            <p className="text-xs text-gray-400 mb-4">The reviewer is notified by email when the scorecard is issued.</p>
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setShowIssueModal(false)}
                                    className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button onClick={handleIssue} disabled={issuing}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                                    {issuing ? "Issuing…" : "Issue Scorecard"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── History ────────────────────────────────────────────────────── */}
            {completed.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 overflow-x-auto">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">Review History</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                                <th className="py-2 pr-4">Period</th>
                                <th className="py-2 pr-4">Overall</th>
                                <th className="py-2 pr-4">Rating</th>
                                <th className="py-2 pr-4">Trend</th>
                                {catalog.themes.map(t => <th key={t.theme} className="py-2 pr-4">{t.theme}</th>)}
                                <th className="py-2 pr-4">Reviewer</th>
                                <th className="py-2">Completed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {completed.map(s => (
                                <tr key={s.id} className="border-b border-gray-50">
                                    <td className="py-2 pr-4 font-medium text-gray-700">{s.reviewPeriod}</td>
                                    <td className="py-2 pr-4 font-bold text-indigo-700">{Number(s.overallScore).toFixed(1)}</td>
                                    <td className="py-2 pr-4">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${LABEL_CFG[s.recommendationLabel] || "bg-gray-100 text-gray-600"}`}>
                                            {s.recommendationLabel}
                                        </span>
                                    </td>
                                    <td className={`py-2 pr-4 text-xs font-semibold ${TREND_CFG[s.trend]?.cls || "text-gray-400"}`}>
                                        {TREND_CFG[s.trend]?.arrow} {s.trend}
                                    </td>
                                    {catalog.themes.map(t => (
                                        <td key={t.theme} className="py-2 pr-4 text-gray-600">
                                            {s.themeAverages?.[t.theme] != null ? Number(s.themeAverages[t.theme]).toFixed(1) : "—"}
                                        </td>
                                    ))}
                                    <td className="py-2 pr-4 text-gray-600">{s.reviewerName || "—"}</td>
                                    <td className="py-2 text-gray-500 text-xs">{fmtDate(s.completedAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
